<script setup lang="ts">
import { Search } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import TheRDBKMobileFilters from '@/components/roadbook/TheRDBKMobileFilters.vue';
import useResponsive from '@/composables/useResponsive';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useProductsStore } from '@/stores/useProductsStore';

import TheMobileSlider from '../mode-list/TheMobileSlider.vue';

const { t } = useI18n();

const filtersStore = useFiltersStore();
const { filters, datesAsStrings } = storeToRefs(filtersStore);

const productsStore = useProductsStore();

const { isMobile } = useResponsive();
const isSearchOpen = ref(false);

function handleSearchFilters() {
  isSearchOpen.value = false;
  filtersStore.loadFromStorage();
}

function resetSearchFilters() {
  const datesFilter = filters.value.filters.findIndex(it => it.filterProperties.type === 'date_range');
  if (datesFilter && datesFilter > -1) {
    datesAsStrings.value = null;
    filters.value.filters.splice(datesFilter, 1);
  }
  filters.value.selections = [];
  filters.value.metrics = filters.value.metrics.filter(it => it.type !== 'crossedTaxonomyCount');
  filtersStore.saveToStorage(filters.value);
  productsStore.fetchProductsList(filters.value);
}
</script>

<template>
  <div v-if="isMobile" class="mobile-filters-wrapper">
    <button
      :aria-haspopup="true"
      aria-controls="search-dialog"
      class="search-button"
      @click="isSearchOpen = !isSearchOpen"
    >
      <div class="search-button-content">
        <Search :size="22" color="var(--yb-color-black)" aria-hidden="true" />
        <div class="search-button-results">
          <p class="theme">
            {{ t('search.default') }}
          </p>
          <p class="dates">
            {{ datesAsStrings ?? t('search.dates') }}
          </p>
        </div>
      </div>
    </button>
  </div>
  <TheMobileSlider
    :is-open="isSearchOpen" :title="t('search.label')" anchor="top" :footer="true"
    @validation="handleSearchFilters" @close="isSearchOpen = false" @reset="resetSearchFilters"
  >
    <TheRDBKMobileFilters />
  </TheMobileSlider>
</template>

<style lang="css" scoped>
.mobile-filters-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--yb-spacing-sm);
  margin-bottom: 1rem;

  .search-button,
  .filters-button {
    all: unset;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--yb-color-white);
    color: var(--yb-color-black);
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: var(--yb-radius-sm);
    height: 54px;
  }

  .filters-button {
    aspect-ratio: 1;
  }

  .search-button {
    flex-grow: 1;
    justify-content: flex-start;
    padding-left: var(--yb-spacing-sm);
  }

  .search-button-content {
    display: flex;
    align-items: center;
    gap: var(--yb-spacing-xs);
    color: var(--yb-color-black);

    .search-button-results {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .theme {
        font-weight: var(--yb-text-bold);
        font-size: var(--yb-text-base);
        margin-block: 0;
      }

      .dates {
        font-weight: var(--yb-text-light);
        font-size: var(--yb-text-sm);
        margin-block: 0;
      }
    }
  }
}
</style>
